/*
 * Copyright (c) CIRROM 2024.
 */

import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChickenOutModal } from "../utils/ChickenOutModal";
import Button from "@mui/material/Button";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import ViewListIcon from "@mui/icons-material/ViewList";
import classes from "./Alerts.module.css";
import Modal from "@mui/material/Modal";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import Divider from "@mui/material/Divider";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { Box, GridLegacy, Typography } from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CategoryIcon from "@mui/icons-material/Category";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {
  addDataAlert,
  DataAlert,
  deleteDataAlert,
  getDataAlertMetadata,
  getDataAlertsForProject,
} from "../../api/dataprojects";
import { toReadableText } from "../utils/utils";
import { useNavigate } from "react-router-dom";

interface AlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (alertDetails: DataAlert) => void;
  alertMetadata: { frequencies: string[]; alert_types: string[] };
  sourceTypeId: number;
}

interface AlertActionAreaProps {
  onAddButtonClicked(): void;
  onRemoveButtonClicked(): void;
  selectedAlertId?: number;
}

const AlertActionArea = ({
  onAddButtonClicked,
  onRemoveButtonClicked,
  selectedAlertId,
}: AlertActionAreaProps) => {
  const [chickenOutModalOpen, setChickenOutModalOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div style={{ marginBottom: "1rem" }}>
      <ChickenOutModal
        message="This alert will be permanently removed. Are you sure?"
        title="Confirm Alert Removal"
        modalOpen={chickenOutModalOpen}
        onModalOpenChange={setChickenOutModalOpen}
        onSuccess={onRemoveButtonClicked}
      />

      <Button
        variant="contained"
        color="primary"
        startIcon={<AddOutlinedIcon />}
        className={classes.alertButtons}
        size="small"
        onClick={onAddButtonClicked}
      >
        Add
      </Button>

      <Button
        variant="contained"
        className={classes.alertButtons}
        color="primary"
        startIcon={<RemoveOutlinedIcon />}
        size="small"
        onClick={() => setChickenOutModalOpen(true)}
        disabled={!selectedAlertId}
      >
        Remove
      </Button>

      <Button
        variant="contained"
        className={classes.alertButtons}
        color="primary"
        startIcon={<ViewListIcon />}
        size="small"
        onClick={() =>
          navigate(
            `/app/configure/data-project-alerts/results/${selectedAlertId}`
          )
        }
        disabled={!selectedAlertId}
      >
        View
      </Button>
    </div>
  );
};

const SectionTitle = ({
  icon,
  title,
}: {
  icon: React.ReactNode;
  title: string;
}) => (
  <Box display="flex" alignItems="center" mb={2}>
    {icon}
    <Typography variant="h6" style={{ marginLeft: "10px" }}>
      {title}
    </Typography>
  </Box>
);

/**
 * NewAlertModal component renders a modal dialog for creating a new alert with various settings.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.isOpen - Determines if the modal is open or not.
 * @param {function} props.onClose - Function to call when the modal should be closed.
 * @param {function} props.onSave - Function to call when the alert settings are saved.
 *
 * Internal state:
 * - alertDetails: Object containing the current state of the alert details, initialized with initialAlertState.
 *
 * Functions:
 * - handleSubmit: Handles the submit action by calling onSave with the current alert details, resetting the alert details, and closing the modal.
 *
 * Returns JSX for rendering the modal dialog with the form to input alert details.
 */
export const NewAlertModal = ({
  isOpen,
  onClose,
  onSave,
  alertMetadata,
  sourceTypeId,
}: AlertModalProps) => {
  const initialAlert = useMemo(() => {
    return {
      description: "",
      recipients: "",
      frequency: "DAILY",
      alert_types: [],
      source_type: "DATA_PROJECT",
      source_type_id: sourceTypeId,
    };
  }, [sourceTypeId]);

  useEffect(() => {
    setAlertDetails(initialAlert);
  }, [initialAlert]);

  const [alertDetails, setAlertDetails] = useState<DataAlert>(initialAlert);

  const handleSubmit = () => {
    onSave(alertDetails);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      className="modal"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Container maxWidth="md" style={{ padding: "0px" }}>
        <Card>
          <CardHeader
            title={
              <Box display="flex" alignItems="center">
                <NotificationsActiveIcon
                  color="primary"
                  style={{ marginRight: "10px", fontSize: "2rem" }}
                />
                <Typography variant="h5"> New Alert</Typography>
              </Box>
            }
            action={
              <IconButton onClick={onClose}>
                <CloseOutlinedIcon />
              </IconButton>
            }
            style={{ backgroundColor: "#f5f5f5" }}
          />
          <Divider />
          <CardContent style={{ padding: "24px" }}>
            <GridLegacy container spacing={1}>
              {/* Alert Basic Information */}
              <GridLegacy item xs={12}>
                <TextField
                  margin="normal"
                  variant={"standard"}
                  fullWidth
                  label="Alert description"
                  value={alertDetails.description}
                  InputLabelProps={{ shrink: true }}
                  style={{ margin: "0px", marginBottom: "15px" }}
                  onChange={(e) =>
                    setAlertDetails({
                      ...alertDetails,
                      description: e.target.value,
                    })
                  }
                  placeholder="Enter a descriptive name for your alert"
                />
                <TextField
                  margin="normal"
                  variant={"standard"}
                  fullWidth
                  label="Email recipients"
                  value={alertDetails.recipients}
                  InputLabelProps={{ shrink: true }}
                  style={{ margin: "0px" }}
                  onChange={(e) =>
                    setAlertDetails({
                      ...alertDetails,
                      recipients: e.target.value,
                    })
                  }
                  placeholder="email1@example.com, email2@example.com"
                  helperText="Add multiple email addresses separated by commas"
                />
              </GridLegacy>
              {/* Frequency Settings */}
              <GridLegacy item xs={12} md={6}>
                <>
                  <SectionTitle
                    icon={<AccessTimeIcon color="primary" />}
                    title="Alert Frequency"
                  />
                  <Select
                    variant={"standard"}
                    data-testid={"select"}
                    style={{ flex: 1, minWidth: "200px" }}
                    value={alertDetails.frequency}
                    onChange={(event) =>
                      setAlertDetails({
                        ...alertDetails,
                        frequency: event.target.value,
                      })
                    }
                  >
                    {alertMetadata.frequencies.map((frequency) => (
                      <MenuItem key={frequency} value={frequency}>
                        {toReadableText(frequency)}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              </GridLegacy>

              {/* Alert Types */}
              <GridLegacy item xs={12} md={6}>
                <>
                  <SectionTitle
                    icon={<CategoryIcon color="primary" />}
                    title="Alert Types"
                  />
                  <FormControl component="fieldset">
                    <FormGroup>
                      {alertMetadata.alert_types.map((alertType) => (
                        <FormControlLabel
                          key={alertType}
                          control={
                            <Checkbox
                              checked={
                                alertDetails.alert_types.indexOf(alertType) > -1
                              }
                              onChange={(event) => {
                                if (event.target.checked) {
                                  setAlertDetails({
                                    ...alertDetails,
                                    alert_types: [
                                      ...alertDetails.alert_types,
                                      alertType,
                                    ],
                                  });
                                } else {
                                  const newAlertTypes =
                                    alertDetails.alert_types;
                                  newAlertTypes.splice(
                                    newAlertTypes.indexOf(alertType),
                                    1
                                  );
                                  setAlertDetails({
                                    ...alertDetails,
                                    alert_types: [...newAlertTypes],
                                  });
                                }
                              }}
                            />
                          }
                          label={toReadableText(alertType)}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </>
              </GridLegacy>
            </GridLegacy>

            {/* Action Buttons */}
            <Divider />
            <Box
              display="flex"
              justifyContent="flex-end"
              p={2}
              borderRadius={1}
            >
              <Button
                variant="contained"
                color="primary"
                className={classes.alertButtons}
                size="small"
                onClick={handleSubmit}
                startIcon={<NotificationsActiveIcon />}
              >
                Create Alert
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Modal>
  );
};

/**
 * A React memoized component that displays a table of alerts and provides
 * actions for managing these alerts.
 *
 * @name AlertsTable
 * @component
 *
 * @param {Array} props.alerts - An array of alert objects to be displayed in the table.
 *

 *
 * Handles actions such as adding, removing data alerts and contains a modal for creating new alerts.
 */
export const AlertsTable = React.memo(function AlertsTable(props: {
  sourceTypeId: number;
}) {
  const { sourceTypeId } = props;
  const [alerts, setAlerts] = useState<DataAlert[]>([]);
  const [selectedAlertId, setSelectedAlertId] = useState<number>();
  const [isNewAlertModalOpen, setIsNewAlertModalOpen] = useState(false);
  const [alertMetadata, setAlertMetadata] = useState<{
    frequencies: string[];
    alert_types: string[];
  }>({ frequencies: [], alert_types: [] });

  const fetchAlerts = useCallback(async () => {
    try {
      const fetchedAlerts = await getDataAlertsForProject(sourceTypeId);
      setAlerts(fetchedAlerts);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  }, [sourceTypeId]);

  useEffect(() => {
    fetchAlerts();
    getDataAlertMetadata().then(setAlertMetadata).catch(console.error);
  }, [fetchAlerts]);

  const handleAddAlert = async (alertDetails: DataAlert) => {
    try {
      await addDataAlert(alertDetails);
      fetchAlerts();
      setIsNewAlertModalOpen(false);
    } catch (error) {
      console.error("Error adding alert:", error);
    }
  };

  const handleDeleteAlert = async () => {
    if (selectedAlertId) {
      try {
        await deleteDataAlert(selectedAlertId);
        fetchAlerts();
        setSelectedAlertId(null);
      } catch (error) {
        console.error("Error deleting alert:", error);
      }
    }
  };

  return (
    <>
      <AlertActionArea
        onAddButtonClicked={() => setIsNewAlertModalOpen(true)}
        onRemoveButtonClicked={() => handleDeleteAlert()}
        selectedAlertId={selectedAlertId}
      />
      <NewAlertModal
        isOpen={isNewAlertModalOpen}
        onClose={() => setIsNewAlertModalOpen(false)}
        onSave={handleAddAlert}
        alertMetadata={alertMetadata}
        sourceTypeId={sourceTypeId}
      />
      <div style={{ height: "400px" }}>
        <AgGridReact
          columnDefs={[
            {
              field: "id",
              headerName: "Id",
              sortable: true,
              resizable: true,
              filter: true,
            },

            {
              field: "description",
              headerName: "Description",
              sortable: true,
              resizable: true,
            },

            {
              field: "frequency",
              headerName: "Frequency",
              sortable: true,
              resizable: true,
              valueFormatter: (params) => toReadableText(params.value),
            },

            {
              field: "alert_types",
              headerName: "Types",
              sortable: true,
              resizable: true,
              valueFormatter: (params) =>
                params.value?.map((option) => toReadableText(option)).join(","),
            },
          ]}
          onFirstDataRendered={(event) => {
            event.api.sizeColumnsToFit();
          }}
          onSelectionChanged={(rowEvent) => {
            if (rowEvent.api && rowEvent.api.getSelectedNodes().length > 0) {
              setSelectedAlertId(rowEvent.api.getSelectedNodes()[0].data.id);
            }
          }}
          rowData={alerts}
          rowSelection={{ mode: "singleRow", checkboxes: true }}
        />
      </div>
    </>
  );
});
